/**
 * LOS CAMBIOS DEL CUADERNO QUE PROPONE EL ASISTENTE (CQ4b · invariante A20).
 *
 * En «@ Cuaderno de campo» el asistente por fin ESCRIBE, y por eso lo hace con
 * una lista CERRADA de operaciones tipadas, siempre con tarjeta: el modelo
 * propone, la persona pulsa «Aplicar» y quien escribe es el navegador con SU
 * sesión (RLS) —nunca la edge, nunca el texto del modelo—. Este módulo es ese
 * contrato, el MISMO a los dos lados: la edge lo lleva en su copia del motor
 * (scripts/sync-cuaderno.mjs) para construir la tarjeta, y la tarjeta
 * (`AssistantCuadernoChangeCard`) lo usa para volver a validarla y aplicarla.
 *
 * Dónde cae cada cosa (D16, regla mixta: hechos al parte, decisiones al borrador):
 *   - BORRADOR (`cuaderno_drafts`, RPC `apply_cuaderno_change` de CQ4a):
 *     «set_answer» (NO aplica del 3.2 al 3.5, análisis, periodo, eficacia por
 *     defecto confirmada, fecha de apertura, nº de orden de parcela),
 *     «exclude_line» (sacar del documento una línea que NO es un fitosanitario,
 *     con motivo) e «include_line». Sus claves, sus tipos, su validación y su
 *     recuento NO se definen aquí: son los de `borrador.ts`, el espejo de la RPC
 *     (una sola fuente; la RPC vuelve a comprobar el alcance del encargado y que
 *     la línea no es un fitosanitario).
 *   - PARTE (edge `update-spray-log-details`, carril APL/F123): «set_efficacy»,
 *     «set_weather», «set_equipment», «set_applicator», a TODOS los partes de la
 *     tarea (o al parte manual). Así el registro de /spray-logs y el cuaderno
 *     dicen lo mismo.
 *   - DATOS LEGALES (`farm_legal_profiles`, CQ1; solo escribe el admin):
 *     «set_legal_field», un campo con su validador (NIF con su letra de control,
 *     ROPO, código postal, códigos cerrados). Desde el 27-09 (CU7) una tarjeta
 *     lleva TODOS los datos de una ronda («Guardar en Ajustes de la empresa»):
 *     se escriben en UNA sentencia sobre la fila del titular, todo o nada.
 *   - AJUSTES DEL CUADERNO (CU7, 27-09-2026; solo escribe el admin, RLS): lo que
 *     el cuaderno pregunta una sola vez y vive en Ajustes → Empresa →
 *     «Cuaderno de campo»:
 *       «upsert_equipment»       una máquina (`company_equipment`): alta con tipo
 *                                cerrado y descripción, o el nº ROMA/censo de una
 *                                que ya existe (checkRoma; la mochila, exenta);
 *       «upsert_service_company» una empresa de servicios (`company_applicators`,
 *                                kind service_company): nombre, NIF con su
 *                                control, ROPO y caducidad (o expedición);
 *       «set_member_license»     el carné de una persona del equipo: ROPO, tipo
 *                                cerrado y caducidad OBLIGATORIA;
 *       «set_sigpac»             el recinto SIGPAC de una nave, escrito a mano y
 *                                por eso confirmado (`greenhouse_sigpac_recintos`).
 *     Una por tarjeta: van a tablas distintas y el navegador no tiene
 *     transacción entre ellas, así que «todo o nada» es una sola escritura.
 *   - EL DOCUMENTO (CU9, decisión de Pablo del 28-09-2026: «el cuaderno de campo
 *     es SU documento»; D16 SUPERSEDIDA): «override_line» (dosis, cantidad total,
 *     producto y nº, plaga, superficie, fecha, eficacia, aplicador, equipo,
 *     observaciones de una línea), «add_line» / «remove_line» (una aplicación que
 *     no está en los partes) y «exclude_line» de CUALQUIER línea, también un
 *     fitosanitario. Van al BORRADOR por la misma RPC, nunca a los partes ni al
 *     stock, y se APLICAN SIN PREGUNTAR (`esCambioDelDocumento`): la tarjeta llega,
 *     se aplica sola con la sesión de la persona y enseña «Hecho» con «Deshacer»
 *     (`deshacer`: las operaciones inversas, construidas por la edge con el
 *     borrador que leyó y validadas aquí igual que las de ida).
 * Lo que NO está aquí no se puede hacer desde el chat: borrar o cambiar un parte
 * (el registro se queda como está: se cambia el DOCUMENTO), borrar o archivar una
 * máquina o un carné.
 * [Hasta el 28-09: tampoco sacar un fitosanitario registrado ni cambiar una dosis,
 * un producto o una fecha, que se corregían en el parte.]
 *
 * PURO: solo importa del propio cuaderno (validadores y códigos) y las reglas
 * puras de maquinaria y carnés (sin imports). Los números (dosis, superficies,
 * nº de registro) NO pasan por aquí: los calcula el motor.
 */
import { checkNif, checkRoma, checkRopo, checkSigpac, isValidCodigoPostal, type SigpacCodes } from "./validators";
import { EQUIPMENT_TYPES, equipmentRegime, type EquipmentType } from "@/lib/machinery/equipmentRules";
import { LICENSE_TYPES, type LicenseType } from "@/lib/applicators/applicatorRules";
import { CAMPOS_ASESOR, CAMPOS_EXPLOTACION, CAMPOS_TITULAR, DatosFinca, GIP_TIPOS, PROTECCIONES, RIEGOS } from "./datosLegales";
import { esOpBorrador, MAX_OPS_BORRADOR, OPS_DOCUMENTO, recuentoDeOps, validarOpBorrador, type CuadernoOp } from "./borrador";
import { CONDICIONES_TIEMPO, EFICACIAS, type CondicionTiempo, type Eficacia } from "./entrada";

// ── Las listas cerradas ─────────────────────────────────────────────────────

/* Las decisiones del documento (claves de `set_answer`) son las del borrador:
   se re-exportan para quien ya las leía de aquí, sin una segunda lista. */
export { CLAVES_RESPUESTA, CLAVES_SI_NO, type ClaveRespuesta } from "./borrador";

/** Los campos de `farm_legal_profiles` que el asistente puede proponer. */
export const CAMPOS_LEGALES = [...CAMPOS_EXPLOTACION, ...CAMPOS_TITULAR, ...CAMPOS_ASESOR, "gip_tipo", "riego_defecto", "proteccion_defecto"] as const;
export type CampoLegal = (typeof CAMPOS_LEGALES)[number];

/**
 * IDENTIFICADORES: se repiten a la persona y solo quedan firmes con un «sí»
 * (PLAN §3, CQ5). Un NIF con una cifra cambiada tiene el mismo aspecto que uno
 * bueno; el REA y el código postal, igual.
 */
export const CAMPOS_IDENTIFICADOR: readonly CampoLegal[] = [
  "nif",
  "titular_nif",
  "asesor_nif",
  "asesor_ropo",
  "rea_nacional",
  "reafa",
  "codigo_postal",
  "titular_codigo_postal",
];

/* Los valores de eficacia y tiempo son los del parte (entrada.ts), no otra lista. */
export { EFICACIAS, CONDICIONES_TIEMPO as TIEMPOS } from "./entrada";
export type EficaciaCambio = Eficacia;
export type TiempoCambio = CondicionTiempo;

/** Qué partes toca un cambio de hechos: los de una tarea, o un parte manual suelto. */
export interface ObjetivoParte {
  task_id?: string | null;
  spray_log_id?: string | null;
  /** Los partes que ESPERA tocar (para el recuento): los N sectores de la tarea. */
  spray_log_ids: string[];
}

/** Las del borrador (`CuadernoOp`, de borrador.ts) + los hechos del parte + un dato legal. */
export type OpCuaderno =
  | CuadernoOp
  | { op: "set_efficacy"; objetivo: ObjetivoParte; valor: EficaciaCambio }
  | { op: "set_weather"; objetivo: ObjetivoParte; valor: TiempoCambio }
  | { op: "set_equipment"; objetivo: ObjetivoParte; valor: string }
  | { op: "set_applicator"; objetivo: ObjetivoParte; valor: string }
  | { op: "set_legal_field"; campo: CampoLegal; valor: string }
  | OpDatosFinca
  | OpMaquina
  | OpEmpresaServicios
  | OpCarne
  | OpSigpac;

/**
 * Captaciones de agua y zonas específicas del 2.2 de UNA finca (CU7): van en el
 * `per_location` de la fila del titular (`farm_legal_profiles`), la misma fila y
 * la misma sentencia que los datos legales de la ronda. `finca` es solo el nombre
 * para la tarjeta. Desde el chat solo se propone la respuesta habitual («no hay
 * captaciones ni zonas»); con captación o zona, el detalle se escribe en Ajustes.
 */
export interface OpDatosFinca {
  op: "set_datos_finca";
  location_id: string;
  finca: string;
  datos: DatosFinca;
}

/**
 * Una máquina (`company_equipment`). Sin `equipment_id`, ALTA: tipo de la lista
 * cerrada y descripción obligatorios, nº opcional (la mochila no lleva). Con
 * `equipment_id`, solo su nº (ROMA, o de censo si su tipo va al censo): tipo,
 * descripción y finca van a null y no se tocan.
 */
export interface OpMaquina {
  op: "upsert_equipment";
  equipment_id: string | null;
  tipo: EquipmentType | null;
  descripcion: string | null;
  /** Nº ROMA (o de censo), limpio con checkRoma; null = sin nº todavía. */
  roma: string | null;
  location_id: string | null;
}

/** Una empresa de servicios (`company_applicators`, kind service_company): alta o, con id, sus datos. */
export interface OpEmpresaServicios {
  op: "upsert_service_company";
  applicator_id: string | null;
  nombre: string;
  nif: string;
  ropo: string;
  /** Caducidad de su inscripción; si solo se sabe la expedición, +10 años (el trigger de la tabla). */
  caduca: string | null;
  expedido: string | null;
}

/** El carné de UNA persona del equipo (`company_applicators`, kind member, por `user_id`). */
export interface OpCarne {
  op: "set_member_license";
  user_id: string;
  /** Su nombre, para la tarjeta y para la fila si aún no tiene carné. */
  nombre: string;
  ropo: string;
  carne: LicenseType;
  /** OBLIGATORIA (la pide la tabla y el cuaderno la imprime). */
  caduca: string;
  expedido: string | null;
  nif: string | null;
}

/** El recinto SIGPAC de una nave, dictado por el admin: entra CONFIRMADO (lo ha dicho él), como el escrito a mano. */
export interface OpSigpac {
  op: "set_sigpac";
  greenhouse_id: string;
  codigos: SigpacCodes;
}

export type TipoCambio = "borrador" | "parte" | "legal" | "maquinaria" | "aplicador" | "sigpac";
export type OperacionCuaderno =
  | "excluir"
  | "incluir"
  // CU9: cambiar lo que dice el documento de una o varias líneas, añadir una, deshacer.
  | "editar"
  | "anadir"
  | "deshacer"
  | "respuesta"
  | "eficacia"
  | "tiempo"
  | "equipo"
  | "aplicador"
  | "dato_legal"
  | "maquina"
  | "empresa_servicios"
  | "carne"
  | "sigpac";

const TIPO_DE_OP: Record<OpCuaderno["op"], TipoCambio> = {
  set_answer: "borrador",
  exclude_line: "borrador",
  include_line: "borrador",
  override_line: "borrador",
  add_line: "borrador",
  remove_line: "borrador",
  set_efficacy: "parte",
  set_weather: "parte",
  set_equipment: "parte",
  set_applicator: "parte",
  set_legal_field: "legal",
  set_datos_finca: "legal",
  upsert_equipment: "maquinaria",
  upsert_service_company: "aplicador",
  set_member_license: "aplicador",
  set_sigpac: "sigpac",
};

/** Tipos de tarjeta de UNA sola operación (tablas sin transacción desde el navegador: todo o nada = una escritura). */
export const TIPOS_DE_UNA_OP: readonly TipoCambio[] = ["maquinaria", "aplicador", "sigpac"];

export const OPS_CERRADAS = Object.keys(TIPO_DE_OP) as OpCuaderno["op"][];

export function tipoDeOp(op: OpCuaderno["op"]): TipoCambio {
  return TIPO_DE_OP[op];
}

/** Una línea de ejemplo para la tarjeta («antes → después»). Sin datos personales (E11). */
export interface EjemploCambio {
  fecha?: string | null;
  nave?: string | null;
  producto?: string | null;
}

/**
 * La tarjeta «cuaderno_change»: UNA propuesta, homogénea (un solo tipo), con
 * todas sus operaciones y el recuento que se espera tocar. Viaja como UN item
 * del grupo de tarjetas (el bucle recorta los grupos a 20 items, no las
 * operaciones de dentro).
 */
export interface CambioCuaderno {
  v: 1;
  change_id: string;
  tipo: TipoCambio;
  operacion: OperacionCuaderno;
  year: number;
  titular_location_id: string | null;
  draft_id: string | null;
  /** Líneas (borrador), partes (hechos), campos (datos legales) o 1 (máquina, carné, empresa o recinto, CU7). */
  expected_count: number;
  ops: OpCuaderno[];
  /** Lo que se pone, en claro («Cuba 2», «regular», «B04123456»). */
  valor_label?: string | null;
  ejemplos: EjemploCambio[];
  /**
   * Fitosanitarios que se pidió quitar y NO se quitan (D16): registrados o, con
   * `sin_numero`, de categoría fitosanitaria sin nº (les falta el nº).
   * Desde CU9 (28-09-2026) la edge ya no la rellena (sale cualquiera); se sigue
   * leyendo por las tarjetas viejas del historial.
   */
  bloqueadas?: Array<{ producto: string; lineas: number; sin_numero?: boolean }>;
  /**
   * CU9: las operaciones que DESHACEN esta tarjeta (solo en las del documento), con
   * el borrador que leyó la edge: la edición anterior (o null), `remove_line` de una
   * añadida, `include_line` de una quitada… «Deshacer» las aplica por la misma vía.
   */
  deshacer?: CuadernoOp[];
  /** CU9: avisos para la persona («ese nº no está en el MAPA»…). No paran nada. */
  avisos?: AvisoCambio[];
}

/** CU9: lo que la tarjeta avisa de un cambio del documento (texto por i18n: `ipmAssistant.cuadernoChange.notice.<codigo>`). */
export const AVISOS_CAMBIO = ["registro_no_en_mapa", "registro_cancelado", "fitosanitario_quitado"] as const;
export interface AvisoCambio {
  codigo: (typeof AVISOS_CAMBIO)[number];
  producto: string;
  numero?: string | null;
}

const avisoValido = (a: unknown): boolean => {
  if (!a || typeof a !== "object" || Array.isArray(a)) return false;
  const o = a as Record<string, unknown>;
  return (
    typeof o.codigo === "string" &&
    (AVISOS_CAMBIO as readonly string[]).includes(o.codigo) &&
    typeof o.producto === "string" &&
    o.producto.length <= 200 &&
    (o.numero == null || (typeof o.numero === "string" && o.numero.length <= 40))
  );
};

/**
 * ¿Es un cambio del DOCUMENTO (CU9)? Del borrador y solo con operaciones de líneas
 * (`OPS_DOCUMENTO`): esos se aplican SIN PREGUNTAR, con «Deshacer». Las respuestas
 * del guion (`set_answer`), los partes, los datos legales y los ajustes siguen con
 * su «Aplicar».
 */
export function esCambioDelDocumento(c: Pick<CambioCuaderno, "tipo" | "ops">): boolean {
  return c.tipo === "borrador" && c.ops.length > 0 && c.ops.every((o) => (OPS_DOCUMENTO as readonly string[]).includes(o.op));
}

/**
 * La tarjeta que DESHACE otra (CU9): sus `deshacer`, marcadas con el `change_id` de
 * la de ida y `deshacer: true` (así queda en la auditoría), con su recuento. null si
 * la tarjeta no se puede deshacer.
 */
export function cambioDeshacer(c: CambioCuaderno): CambioCuaderno | null {
  if (!esCambioDelDocumento(c) || !c.deshacer?.length) return null;
  const ops = c.deshacer.map((o) => ({ ...o, cambio: c.change_id, deshacer: true })) as CuadernoOp[];
  return {
    v: 1,
    change_id: c.change_id,
    tipo: "borrador",
    operacion: "deshacer",
    year: c.year,
    titular_location_id: c.titular_location_id,
    draft_id: null,
    expected_count: recuentoDeOps(ops),
    ops,
    valor_label: c.valor_label ?? null,
    ejemplos: c.ejemplos,
  };
}

/** Tope de operaciones por tarjeta (el de la RPC): más es un filtro mal entendido, no una propuesta. */
export const MAX_OPS_POR_CAMBIO = MAX_OPS_BORRADOR;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const esUuid = (v: unknown): v is string => typeof v === "string" && UUID.test(v);

// ── Validación de un valor legal ────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LARGO_MAX: Partial<Record<CampoLegal, number>> = {
  razon_social: 200, titular_nombre: 200, asesor_nombre: 200,
  direccion: 300, titular_direccion: 300,
  localidad: 120, titular_localidad: 120,
  provincia: 80, titular_provincia: 80, representacion_tipo: 80,
  telefono_fijo: 30, telefono_movil: 30, titular_telefono: 30,
  email: 200, titular_email: 200,
  rea_nacional: 40, reafa: 40,
};

export type ValorLegal = { ok: true; valor: string } | { ok: false; motivo: "vacio" | "formato" | "control" | "largo" | "codigo" };

/**
 * El valor de un campo legal, limpio y comprobado con LA MISMA regla que el
 * formulario de Ajustes (validators.ts) y el CHECK de la tabla. Lo que no pasa
 * no se propone ni se escribe: un NIF con la letra mal NO se guarda.
 */
export function validarValorLegal(campo: CampoLegal, raw: string | null | undefined): ValorLegal {
  const v = String(raw ?? "").trim().replace(/\s+/g, " ");
  if (!v) return { ok: false, motivo: "vacio" };
  if (campo === "nif" || campo === "titular_nif" || campo === "asesor_nif") {
    const r = checkNif(v);
    if (r.ok) return { ok: true, valor: r.value };
    return { ok: false, motivo: "reason" in r && r.reason === "control" ? "control" : "formato" };
  }
  if (campo === "asesor_ropo") {
    const r = checkRopo(v);
    return r.ok ? { ok: true, valor: r.value } : { ok: false, motivo: "formato" };
  }
  if (campo === "codigo_postal" || campo === "titular_codigo_postal") {
    const c = v.replace(/\s/g, "");
    return isValidCodigoPostal(c) ? { ok: true, valor: c } : { ok: false, motivo: "formato" };
  }
  if (campo === "gip_tipo") {
    const hit = GIP_TIPOS.find((g) => g.toLowerCase() === v.toLowerCase());
    return hit ? { ok: true, valor: hit } : { ok: false, motivo: "codigo" };
  }
  if (campo === "riego_defecto") {
    const hit = RIEGOS.find((g) => g.toLowerCase() === v.toLowerCase());
    return hit ? { ok: true, valor: hit } : { ok: false, motivo: "codigo" };
  }
  if (campo === "proteccion_defecto") {
    const hit = PROTECCIONES.find((g) => g.toLowerCase() === v.toLowerCase());
    return hit ? { ok: true, valor: hit } : { ok: false, motivo: "codigo" };
  }
  if (campo === "email" || campo === "titular_email") {
    return EMAIL_RE.test(v) && v.length <= 200 ? { ok: true, valor: v } : { ok: false, motivo: "formato" };
  }
  if (campo === "rea_nacional" || campo === "reafa") {
    const c = v.toUpperCase();
    if (c.length > 40) return { ok: false, motivo: "largo" };
    // Sin formato publicado único, pero sin cifras no es un nº de registro («no sé», «pendiente»).
    return (c.match(/\d/g) ?? []).length >= 3 ? { ok: true, valor: c } : { ok: false, motivo: "formato" };
  }
  const max = LARGO_MAX[campo] ?? 200;
  return v.length <= max ? { ok: true, valor: v } : { ok: false, motivo: "largo" };
}

/** ¿Hay que repetírselo a la persona antes de proponerlo? */
export const esIdentificador = (campo: string): boolean => (CAMPOS_IDENTIFICADOR as readonly string[]).includes(campo);

// ── Los ajustes del cuaderno (CU7, 27-09-2026) ──────────────────────────────
//
// Cada operación se CONSTRUYE aquí desde lo que se dijo (limpio y comprobado) y
// se VALIDA volviéndola a construir: si la tarjeta no es exactamente lo que
// saldría de sus propios datos, no se aplica. Una sola regla para la edge (que
// la construye) y el navegador (que la aplica).

const ISO_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'AAAA-MM-DD' que existe, sin pasar por zonas horarias. */
export function esFechaReal(v: unknown): v is string {
  if (typeof v !== "string") return false;
  const m = ISO_DIA.exec(v);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1950 || y > 2100 || mo < 1 || mo > 12 || d < 1) return false;
  const bisiesto = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  return d <= [31, bisiesto ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mo - 1];
}

const NOMBRE_MAX = 200;
/** Un nombre dicho, en una línea y sin espacios de más. */
export const nombreLimpio = (raw: unknown): string => String(raw ?? "").trim().replace(/\s+/g, " ");

export type MotivoAjuste = "vacio" | "formato" | "control" | "largo" | "codigo" | "fecha" | "exenta";
/** Lo que no pasa: qué campo y por qué (nunca el valor: E11). */
export interface FalloAjuste { ok: false; campo: string; motivo: MotivoAjuste }
/* `"campo" in r` para saber si falló: sin strictNullChecks, TS no estrecha la unión por un booleano. */
export type Construida<T> = { ok: true; op: T } | FalloAjuste;

const mal = (campo: string, motivo: MotivoAjuste): FalloAjuste => ({ ok: false, campo, motivo });

function nifLimpio(campo: string, raw: unknown): { valor: string } | FalloAjuste {
  const r = checkNif(String(raw ?? ""));
  if (r.ok) return { valor: r.value };
  return mal(campo, "reason" in r && r.reason === "control" ? "control" : r.value ? "formato" : "vacio");
}

function nombreValido(campo: string, raw: unknown): { valor: string } | FalloAjuste {
  const v = nombreLimpio(raw);
  if (!v) return mal(campo, "vacio");
  return v.length <= NOMBRE_MAX ? { valor: v } : mal(campo, "largo");
}

/**
 * El nº de una máquina según su tipo. La mochila (o carretilla de menos de 100 L)
 * va EXENTA y no lleva nº; las demás, el ROMA (o el de censo autonómico si su tipo
 * va al censo), comprobado con checkRoma. Vacío = sin nº todavía (null).
 */
export function validarNumeroMaquina(tipo: string | null | undefined, raw: unknown): { valor: string | null } | FalloAjuste {
  const v = nombreLimpio(raw);
  if (!v) return { valor: null };
  if (equipmentRegime(tipo) === "exenta") return mal("roma", "exenta");
  const r = checkRoma(v);
  return r.ok ? { valor: r.value } : mal("roma", "formato");
}

/** El 2.2 de una finca: los datos con el esquema del formulario (`DatosFinca`, zod), limpios. */
export function construirOpDatosFinca(d: { locationId?: string | null; finca?: string | null; datos?: unknown }): Construida<OpDatosFinca> {
  if (!esUuid(d.locationId)) return mal("location_id", "formato");
  const finca = nombreValido("finca", d.finca);
  if ("campo" in finca) return finca;
  const r = DatosFinca.safeParse(d.datos ?? {});
  if (!r.success) return mal("datos", "formato");
  // Sin captación, sus detalles no se guardan; sin zona parcial, tampoco sus hectáreas.
  const datos: DatosFinca = r.data.captacion
    ? r.data
    : { captacion: false, zona: r.data.zona, ...(r.data.zona === "parcial" && r.data.zona_ha != null ? { zona_ha: r.data.zona_ha } : {}) };
  return { ok: true, op: { op: "set_datos_finca", location_id: d.locationId as string, finca: finca.valor, datos } };
}

/** Una máquina: alta (sin id) o el nº de una que ya existe (con id). */
export function construirOpMaquina(d: {
  equipmentId?: string | null;
  tipo?: string | null;
  descripcion?: string | null;
  roma?: string | null;
  locationId?: string | null;
}): Construida<OpMaquina> {
  if (d.equipmentId != null) {
    if (!esUuid(d.equipmentId)) return mal("equipment_id", "formato");
    const n = validarNumeroMaquina(null, d.roma);
    if ("campo" in n) return n;
    if (!n.valor) return mal("roma", "vacio");
    return { ok: true, op: { op: "upsert_equipment", equipment_id: d.equipmentId, tipo: null, descripcion: null, roma: n.valor, location_id: null } };
  }
  if (!(EQUIPMENT_TYPES as readonly string[]).includes(String(d.tipo ?? ""))) return mal("tipo", d.tipo ? "codigo" : "vacio");
  const tipo = d.tipo as EquipmentType;
  const desc = nombreValido("descripcion", d.descripcion);
  if ("campo" in desc) return desc;
  const n = validarNumeroMaquina(tipo, d.roma);
  if ("campo" in n) return n;
  if (d.locationId != null && !esUuid(d.locationId)) return mal("location_id", "formato");
  return { ok: true, op: { op: "upsert_equipment", equipment_id: null, tipo, descripcion: desc.valor, roma: n.valor, location_id: d.locationId ?? null } };
}

/** Las dos fechas de una inscripción o un carné: reales y, si van las dos, la caducidad después. */
function fechas(caduca: unknown, expedido: unknown, caducaObligatoria: boolean): { caduca: string | null; expedido: string | null } | FalloAjuste {
  const c = caduca == null || caduca === "" ? null : caduca;
  const e = expedido == null || expedido === "" ? null : expedido;
  if (c != null && !esFechaReal(c)) return mal("caduca", "fecha");
  if (e != null && !esFechaReal(e)) return mal("expedido", "fecha");
  if (caducaObligatoria && c == null) return mal("caduca", "vacio");
  if (c == null && e == null) return mal("caduca", "vacio");
  if (c != null && e != null && (c as string) < (e as string)) return mal("caduca", "fecha");
  return { caduca: c as string | null, expedido: e as string | null };
}

function ropoLimpio(raw: unknown): { valor: string } | FalloAjuste {
  const r = checkRopo(String(raw ?? ""));
  return r.ok ? { valor: r.value } : mal("ropo", r.value ? "formato" : "vacio");
}

/** Una empresa de servicios que aplica: nombre, NIF con su control, ROPO y caducidad (o expedición). */
export function construirOpEmpresaServicios(d: {
  applicatorId?: string | null;
  nombre?: string | null;
  nif?: string | null;
  ropo?: string | null;
  caduca?: string | null;
  expedido?: string | null;
}): Construida<OpEmpresaServicios> {
  if (d.applicatorId != null && !esUuid(d.applicatorId)) return mal("applicator_id", "formato");
  const nombre = nombreValido("nombre", d.nombre);
  if ("campo" in nombre) return nombre;
  const nif = nifLimpio("nif", d.nif);
  if ("campo" in nif) return nif;
  const ropo = ropoLimpio(d.ropo);
  if ("campo" in ropo) return ropo;
  const f = fechas(d.caduca, d.expedido, false);
  if ("campo" in f) return f;
  return {
    ok: true,
    op: { op: "upsert_service_company", applicator_id: d.applicatorId ?? null, nombre: nombre.valor, nif: nif.valor, ropo: ropo.valor, caduca: f.caduca, expedido: f.expedido },
  };
}

/** El carné de una persona del equipo: ROPO, tipo cerrado y caducidad OBLIGATORIA (NIF opcional). */
export function construirOpCarne(d: {
  userId?: string | null;
  nombre?: string | null;
  ropo?: string | null;
  carne?: string | null;
  caduca?: string | null;
  expedido?: string | null;
  nif?: string | null;
}): Construida<OpCarne> {
  if (!esUuid(d.userId)) return mal("user_id", "formato");
  const nombre = nombreValido("nombre", d.nombre);
  if ("campo" in nombre) return nombre;
  const ropo = ropoLimpio(d.ropo);
  if ("campo" in ropo) return ropo;
  if (!(LICENSE_TYPES as readonly string[]).includes(String(d.carne ?? ""))) return mal("carne", d.carne ? "codigo" : "vacio");
  const f = fechas(d.caduca, d.expedido, true);
  if ("campo" in f) return f;
  let nif: string | null = null;
  if (d.nif != null && String(d.nif).trim()) {
    const n = nifLimpio("nif", d.nif);
    if ("campo" in n) return n;
    nif = n.valor;
  }
  return {
    ok: true,
    op: { op: "set_member_license", user_id: d.userId as string, nombre: nombre.valor, ropo: ropo.valor, carne: d.carne as LicenseType, caduca: f.caduca as string, expedido: f.expedido, nif },
  };
}

/** El recinto SIGPAC de una nave: los siete códigos comprobados y limpios (sin ceros delante). */
export function construirOpSigpac(d: { greenhouseId?: string | null; codigos?: Partial<Record<keyof SigpacCodes, string | number | null>> | null }): Construida<OpSigpac> {
  if (!esUuid(d.greenhouseId)) return mal("greenhouse_id", "formato");
  if (!d.codigos || typeof d.codigos !== "object") return mal("codigos", "vacio");
  const r = checkSigpac(d.codigos);
  if (!("codes" in r)) return mal("codigos", "formato");
  return { ok: true, op: { op: "set_sigpac", greenhouse_id: d.greenhouseId as string, codigos: r.codes } };
}

/** ¿La operación es EXACTAMENTE la que sale de sus propios datos? (La regla de construir, no una copia.) */
function igualQueConstruida(o: Record<string, unknown>): boolean {
  let r: Construida<OpDatosFinca | OpMaquina | OpEmpresaServicios | OpCarne | OpSigpac>;
  switch (o.op) {
    case "set_datos_finca":
      r = construirOpDatosFinca({ locationId: o.location_id as string, finca: o.finca as string, datos: o.datos });
      break;
    case "upsert_equipment":
      r = construirOpMaquina({
        equipmentId: o.equipment_id as string | null,
        tipo: o.tipo as string | null,
        descripcion: o.descripcion as string | null,
        roma: o.roma as string | null,
        locationId: o.location_id as string | null,
      });
      // Con id, tipo, descripción y finca van a null: lo contrario es otra cosa.
      if (!("campo" in r) && o.equipment_id != null && (o.tipo != null || o.descripcion != null || o.location_id != null)) return false;
      break;
    case "upsert_service_company":
      r = construirOpEmpresaServicios({
        applicatorId: o.applicator_id as string | null,
        nombre: o.nombre as string,
        nif: o.nif as string,
        ropo: o.ropo as string,
        caduca: o.caduca as string | null,
        expedido: o.expedido as string | null,
      });
      break;
    case "set_member_license":
      r = construirOpCarne({
        userId: o.user_id as string,
        nombre: o.nombre as string,
        ropo: o.ropo as string,
        carne: o.carne as string,
        caduca: o.caduca as string,
        expedido: o.expedido as string | null,
        nif: o.nif as string | null,
      });
      break;
    case "set_sigpac":
      r = construirOpSigpac({ greenhouseId: o.greenhouse_id as string, codigos: o.codigos as Record<string, string> });
      break;
    default:
      return false;
  }
  if ("campo" in r) return false;
  const orden = (x: unknown): unknown =>
    x && typeof x === "object" && !Array.isArray(x)
      ? Object.fromEntries(Object.keys(x as object).sort().map((k) => [k, orden((x as Record<string, unknown>)[k])]))
      : x;
  const conNulos = { ...Object.fromEntries(Object.keys(r.op).map((k) => [k, null])), ...o };
  return JSON.stringify(orden(conNulos)) === JSON.stringify(orden(r.op));
}

// ── Dónde queda: Ajustes → Mi cuenta → «Cuaderno de campo» (hasta el 27-09 por la tarde, Configuración de empresa) ──

/**
 * Los grupos de la página «Cuaderno de campo» de Ajustes (`/settings/cuaderno`,
 * desde el 27-09-2026; antes, un bloque plegable de /settings/company), con su
 * enlace profundo `/settings/cuaderno?seccion=…` (contrato con la pantalla,
 * `SettingsCuaderno`). Van en el orden en que la página los enseña.
 */
export const SECCIONES_AJUSTES = ["explotacion", "titular", "asesor", "cultivo", "maquinaria", "carnes", "servicios", "sigpac"] as const;
export type SeccionAjustes = (typeof SECCIONES_AJUSTES)[number];

/**
 * El nombre de cada grupo en pantalla (clave i18n): la etiqueta del botón que
 * lleva a él, en la tarjeta del chat y en la edge (misma lista, por la copia Deno).
 */
export const ETIQUETA_SECCION_AJUSTES: Record<SeccionAjustes, string> = {
  explotacion: "cuadernoAjustes.groups.explotacion",
  titular: "cuadernoAjustes.groups.titular",
  asesor: "cuadernoAjustes.groups.asesor",
  cultivo: "cuadernoAjustes.groups.cultivo",
  maquinaria: "cuadernoAjustes.groups.maquinaria",
  carnes: "cuadernoAjustes.groups.carnes",
  servicios: "cuadernoAjustes.groups.servicios",
  sigpac: "cuadernoAjustes.groups.sigpac",
};
/** El nombre de la página (clave i18n): va delante del grupo en esos botones. */
export const ETIQUETA_PAGINA_AJUSTES = "cuadernoAjustes.title";

/** La página. Sin `?seccion`, se abre por arriba. */
export const RUTA_AJUSTES_CUADERNO = "/settings/cuaderno";

export const rutaAjustesCuaderno = (seccion: SeccionAjustes): string => `${RUTA_AJUSTES_CUADERNO}?seccion=${seccion}`;

/**
 * El enlace VIEJO (`/settings/company?cuaderno=datos|maquinaria|servicios`, del
 * 27-09 por la mañana): sigue en conversaciones guardadas y en marcadores, así
 * que /settings/company lo redirige aquí. «datos» era todo lo legal y el SIGPAC:
 * va a «explotacion», lo primero de la página. Un valor desconocido, a la página.
 */
export function rutaDesdeEnlaceViejo(valor: string | null | undefined): string {
  const v = (valor ?? "").trim();
  const seccion = v === "datos" ? "explotacion" : (SECCIONES_AJUSTES as readonly string[]).includes(v) ? (v as SeccionAjustes) : null;
  return seccion ? rutaAjustesCuaderno(seccion) : RUTA_AJUSTES_CUADERNO;
}

const CAMPOS_TITULAR_SET = new Set<string>(CAMPOS_TITULAR);
const CAMPOS_ASESOR_SET = new Set<string>([...CAMPOS_ASESOR, "gip_tipo"]);
const CAMPOS_CULTIVO_SET = new Set<string>(["riego_defecto", "proteccion_defecto"]);

function seccionDeOpLegal(o: OpCuaderno): SeccionAjustes {
  if (o.op === "set_datos_finca") return "cultivo";
  if (o.op !== "set_legal_field") return "explotacion";
  if (CAMPOS_TITULAR_SET.has(o.campo)) return "titular";
  if (CAMPOS_ASESOR_SET.has(o.campo)) return "asesor";
  if (CAMPOS_CULTIVO_SET.has(o.campo)) return "cultivo";
  return "explotacion";
}

/**
 * Dónde queda lo que aplica una tarjeta: el grupo de la página. Los datos
 * legales, en el suyo (explotación, titular, asesor o cultivo; si la tarjeta
 * mezcla grupos, «explotacion», que es lo primero); el SIGPAC, en «sigpac»; las
 * máquinas, en «maquinaria»; las empresas de servicios, en «servicios». El
 * carné de una PERSONA lleva a su ficha del equipo: null. Los cambios del
 * borrador y de los partes no son ajustes: null.
 */
export function seccionAjustes(c: Pick<CambioCuaderno, "tipo" | "ops">): SeccionAjustes | null {
  if (c.tipo === "legal") {
    const grupos = new Set(c.ops.map(seccionDeOpLegal));
    return grupos.size === 1 ? [...grupos][0] : "explotacion";
  }
  if (c.tipo === "sigpac") return "sigpac";
  if (c.tipo === "maquinaria") return "maquinaria";
  if (c.tipo === "aplicador") return c.ops.every((o) => o.op === "upsert_service_company") ? "servicios" : null;
  return null;
}

// ── Validación de una tarjeta entera ────────────────────────────────────────

const enLista = <T extends string>(lista: readonly T[], v: unknown): v is T => typeof v === "string" && (lista as readonly string[]).includes(v);

function validarObjetivo(o: unknown): o is ObjetivoParte {
  if (!o || typeof o !== "object") return false;
  const x = o as ObjetivoParte;
  const porTarea = esUuid(x.task_id);
  const porParte = esUuid(x.spray_log_id);
  if (porTarea === porParte) return false; // uno y solo uno
  return Array.isArray(x.spray_log_ids) && x.spray_log_ids.length > 0 && x.spray_log_ids.every(esUuid);
}

function validarOp(op: unknown, year: number): string | null {
  if (!op || typeof op !== "object") return "op_no_objeto";
  const o = op as Record<string, unknown>;
  // Las del borrador, con la regla de la RPC (borrador.ts), no con una copia.
  if (esOpBorrador(o.op)) return validarOpBorrador(o, year);
  switch (o.op) {
    case "set_efficacy":
      return validarObjetivo(o.objetivo) && enLista(EFICACIAS, o.valor) ? null : "valor_invalido";
    case "set_weather":
      return validarObjetivo(o.objetivo) && enLista(CONDICIONES_TIEMPO, o.valor) ? null : "valor_invalido";
    case "set_equipment":
    case "set_applicator":
      return validarObjetivo(o.objetivo) && esUuid(o.valor) ? null : "valor_invalido";
    case "set_legal_field": {
      if (!enLista(CAMPOS_LEGALES, o.campo)) return "campo_no_permitido";
      const r = validarValorLegal(o.campo, typeof o.valor === "string" ? o.valor : "");
      return r.ok && r.valor === o.valor ? null : "valor_invalido";
    }
    case "set_datos_finca":
    case "upsert_equipment":
    case "upsert_service_company":
    case "set_member_license":
    case "set_sigpac":
      return igualQueConstruida(o) ? null : "valor_invalido";
    default:
      return "op_no_permitida";
  }
}

export type CambioValidado = { ok: true; cambio: CambioCuaderno } | { ok: false; motivo: string };

/**
 * ¿Es una tarjeta que se puede aplicar? Lista cerrada, un solo tipo, recuento
 * coherente. La tarjeta NO se fía de lo que le llega (la escribió el servidor,
 * pero viaja por la red y se guarda en el historial): la vuelve a mirar entera.
 */
export function validarCambio(raw: unknown): CambioValidado {
  if (!raw || typeof raw !== "object") return { ok: false, motivo: "no_objeto" };
  const c = raw as CambioCuaderno;
  if (c.v !== 1) return { ok: false, motivo: "version" };
  if (!Array.isArray(c.ops) || c.ops.length === 0) return { ok: false, motivo: "sin_ops" };
  if (c.ops.length > MAX_OPS_POR_CAMBIO) return { ok: false, motivo: "demasiadas_ops" };
  if (!Number.isInteger(c.year) || c.year < 2000 || c.year > 2100) return { ok: false, motivo: "anio" };
  if (c.titular_location_id != null && !esUuid(c.titular_location_id)) return { ok: false, motivo: "titular" };
  if (c.draft_id != null && !esUuid(c.draft_id)) return { ok: false, motivo: "borrador" };
  for (const op of c.ops) {
    const m = validarOp(op, c.year);
    if (m) return { ok: false, motivo: m };
  }
  const tipos = new Set(c.ops.map((o) => tipoDeOp(o.op)));
  if (tipos.size !== 1 || !tipos.has(c.tipo)) return { ok: false, motivo: "tipos_mezclados" };
  // CU7: máquinas, carnés y SIGPAC, uno por tarjeta (todo o nada = una escritura).
  if (TIPOS_DE_UNA_OP.includes(c.tipo) && c.ops.length !== 1) return { ok: false, motivo: "una_por_tarjeta" };
  if (!operacionCuadra(c)) return { ok: false, motivo: "operacion" };
  // CU9: las de deshacer, con la MISMA regla que las de ida, y solo en un cambio del documento.
  if (c.deshacer != null) {
    if (!Array.isArray(c.deshacer) || c.deshacer.length === 0 || c.deshacer.length > MAX_OPS_POR_CAMBIO) return { ok: false, motivo: "deshacer" };
    if (!esCambioDelDocumento(c)) return { ok: false, motivo: "deshacer" };
    for (const op of c.deshacer) {
      if (!op || typeof op !== "object" || !(OPS_DOCUMENTO as readonly string[]).includes((op as { op?: string }).op ?? "")) return { ok: false, motivo: "deshacer" };
      if (validarOpBorrador(op, c.year)) return { ok: false, motivo: "deshacer" };
    }
  }
  if (c.avisos != null && (!Array.isArray(c.avisos) || c.avisos.length > 20 || !c.avisos.every(avisoValido))) {
    return { ok: false, motivo: "avisos" };
  }
  // Una ronda de datos legales: cada campo una vez (dos valores del mismo campo no se eligen aquí).
  if (c.tipo === "legal") {
    const claves = c.ops.map((o) => (o.op === "set_datos_finca" ? `finca:${o.location_id}` : (o as { campo: string }).campo));
    if (new Set(claves).size !== claves.length) return { ok: false, motivo: "campo_repetido" };
  }
  if (c.expected_count !== recuentoEsperado(c.ops)) return { ok: false, motivo: "recuento" };
  return { ok: true, cambio: c };
}

/** La operación de la tarjeta (lo que lee la persona en el título) cuadra con lo que hace. */
function operacionCuadra(c: CambioCuaderno): boolean {
  switch (c.tipo) {
    case "legal":
      return c.operacion === "dato_legal";
    case "maquinaria":
      return c.operacion === "maquina";
    case "sigpac":
      return c.operacion === "sigpac";
    case "aplicador":
      return c.operacion === (c.ops[0]?.op === "set_member_license" ? "carne" : "empresa_servicios");
    case "borrador": {
      // CU9: el título que lee la persona dice lo que hace.
      const ops = new Set(c.ops.map((o) => o.op));
      const solo = (...permitidas: string[]) => [...ops].every((o) => permitidas.includes(o));
      if (c.operacion === "editar") return solo("override_line");
      if (c.operacion === "anadir") return solo("add_line");
      if (c.operacion === "excluir") return solo("exclude_line", "remove_line");
      if (c.operacion === "incluir") return solo("include_line");
      if (c.operacion === "deshacer") return solo(...OPS_DOCUMENTO);
      if (c.operacion === "respuesta") return solo("set_answer");
      return false;
    }
    default:
      return true;
  }
}

/**
 * Lo que se espera tocar. En el borrador, el `p_expected_count` de la RPC
 * (`recuentoDeOps`: una por clave y una por línea, sin repetir); en los hechos,
 * los partes de todos los objetivos (sin repetir); en los datos legales, un
 * campo por campo distinto; en los ajustes del cuaderno (CU7), una fila por
 * operación.
 */
export function recuentoEsperado(ops: OpCuaderno[]): number {
  /* (el 2.2 de una finca cuenta como un «campo» más de la fila: `finca:<id>`) */
  const delBorrador: CuadernoOp[] = [];
  const partes = new Set<string>();
  const campos = new Set<string>();
  // CU7: una máquina, un carné, una empresa o un recinto = una fila.
  let ajustes = 0;
  for (const o of ops) {
    if (esOpBorrador(o.op)) delBorrador.push(o as CuadernoOp);
    else if ("objetivo" in o) for (const id of o.objetivo.spray_log_ids) partes.add(id);
    else if (o.op === "set_legal_field") campos.add(o.campo);
    else if (o.op === "set_datos_finca") campos.add(`finca:${o.location_id}`);
    else ajustes++;
  }
  return recuentoDeOps(delBorrador) + partes.size + campos.size + ajustes;
}

// ── Qué se manda a cada escritor ────────────────────────────────────────────

/** Los cambios de un parte, en el contrato de `update-spray-log-details` (F123). */
export function cambiosDeParte(op: Extract<OpCuaderno, { objetivo: ObjetivoParte }>): Record<string, string> {
  switch (op.op) {
    case "set_efficacy":
      return { efficacy: op.valor };
    case "set_weather":
      return { weather_condition: op.valor };
    case "set_equipment":
      return { equipment_id: op.valor };
    case "set_applicator":
      return { applicator_id: op.valor };
  }
}

/** El cuerpo de UNA llamada a `update-spray-log-details`. */
export function cuerpoDeParte(op: Extract<OpCuaderno, { objetivo: ObjetivoParte }>): Record<string, unknown> {
  return {
    ...(op.objetivo.task_id ? { task_id: op.objetivo.task_id } : { spray_log_id: op.objetivo.spray_log_id }),
    changes: cambiosDeParte(op),
  };
}
